/* =============================================================
   Select Anime — Wishlist (Favorites)
   App-free, localStorage-based wishlist.

   Storage:   localStorage key "sa:wishlist" => JSON array of product handles
   Event:     dispatches "sa:wishlist:change" on document on every mutation
   Elements:  <sa-wishlist-toggle data-handle="..."> custom element
   Count:     updates any [data-sa-wishlist-count] node (header badge)

   Loaded with defer; written to be robust without DOMContentLoaded by
   relying on custom-element upgrade + event delegation.
   ============================================================= */
(function () {
  'use strict';

  var STORAGE_KEY = 'sa:wishlist';
  var CHANGE_EVENT = 'sa:wishlist:change';
  var MAX_ITEMS = 100; // cap stored favorites to bound the favorites-page fetches
  var HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,254}$/; // valid Shopify product handle

  /* ---------- Store ---------------------------------------- */
  var SAWishlist = {
    /**
     * Read the list of handles from localStorage.
     * @returns {string[]}
     */
    getAll: function () {
      var raw;
      try {
        raw = window.localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return [];
      }
      if (!raw) return [];
      try {
        var parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Keep only valid product handles, de-duplicated, capped at MAX_ITEMS.
        var seen = Object.create(null);
        var out = [];
        for (var i = 0; i < parsed.length && out.length < MAX_ITEMS; i++) {
          var h = parsed[i];
          if (typeof h === 'string' && HANDLE_RE.test(h) && !seen[h]) {
            seen[h] = true;
            out.push(h);
          }
        }
        return out;
      } catch (e) {
        return [];
      }
    },

    /**
     * Persist the list and notify listeners.
     * @param {string[]} handles
     * @param {string} [changedHandle]
     */
    _save: function (handles, changedHandle) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
      } catch (e) {
        /* storage may be full or disabled; fail silently */
      }
      this._dispatch(changedHandle);
    },

    /**
     * @param {string} handle
     * @returns {boolean}
     */
    has: function (handle) {
      if (!handle) return false;
      return this.getAll().indexOf(handle) !== -1;
    },

    /**
     * @param {string} handle
     * @returns {boolean} true if added
     */
    add: function (handle) {
      if (typeof handle !== 'string' || !HANDLE_RE.test(handle)) return false;
      var list = this.getAll();
      if (list.indexOf(handle) !== -1) return false;
      if (list.length >= MAX_ITEMS) return false;
      list.push(handle);
      this._save(list, handle);
      return true;
    },

    /**
     * @param {string} handle
     * @returns {boolean} true if removed
     */
    remove: function (handle) {
      if (!handle) return false;
      var list = this.getAll();
      var idx = list.indexOf(handle);
      if (idx === -1) return false;
      list.splice(idx, 1);
      this._save(list, handle);
      return true;
    },

    /**
     * @param {string} handle
     * @returns {boolean} new membership state (true = now in list)
     */
    toggle: function (handle) {
      if (!handle) return false;
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    },

    count: function () {
      return this.getAll().length;
    },

    _dispatch: function (changedHandle) {
      var detail = { handles: this.getAll() };
      if (changedHandle) detail.handle = changedHandle;
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: detail, bubbles: false }));
    },
  };

  // Expose on window so the favorites page and other scripts can reuse it.
  window.SAWishlist = SAWishlist;

  /* ---------- Icon markup ---------------------------------- */
  function heartSVG() {
    return (
      '<svg class="sa-wishlist__icon" xmlns="http://www.w3.org/2000/svg" ' +
      'viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
      '<path d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/>' +
      '</svg>'
    );
  }

  /* ---------- Custom element: <sa-wishlist-toggle> --------- */
  class SAWishlistToggle extends HTMLElement {
    connectedCallback() {
      if (this._initialised) {
        this.render();
        return;
      }
      this._initialised = true;

      this.button = this.querySelector('button');
      if (!this.button) {
        // Build a button if the snippet did not provide one.
        this.button = document.createElement('button');
        this.button.type = 'button';
        this.appendChild(this.button);
      }
      if (!this.button.querySelector('.sa-wishlist__icon')) {
        this.button.insertAdjacentHTML('afterbegin', heartSVG());
      }

      this._onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this._onClick);

      this.render();
    }

    disconnectedCallback() {
      if (this.button && this._onClick) {
        this.button.removeEventListener('click', this._onClick);
      }
    }

    get handle() {
      return this.getAttribute('data-handle') || '';
    }

    onClick(event) {
      // Toggle never navigates, even when nested inside a card link.
      event.preventDefault();
      event.stopPropagation();
      var nowActive = SAWishlist.toggle(this.handle);
      this.reflect(nowActive);
      // Visible press feedback (pop animation, see sa-wishlist.css).
      var self = this;
      this.classList.remove('is-popping');
      void this.offsetWidth; // restart the animation on rapid clicks
      this.classList.add('is-popping');
      window.setTimeout(function () {
        self.classList.remove('is-popping');
      }, 400);
    }

    render() {
      this.reflect(SAWishlist.has(this.handle));
    }

    reflect(active) {
      if (!this.button) return;
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      var addLabel = this.getAttribute('data-label-add') || 'Add to favorites';
      var removeLabel = this.getAttribute('data-label-remove') || 'Remove from favorites';
      this.button.setAttribute('aria-label', active ? removeLabel : addLabel);
      this.classList.toggle('is-active', active);
    }
  }

  if (window.customElements && !window.customElements.get('sa-wishlist-toggle')) {
    window.customElements.define('sa-wishlist-toggle', SAWishlistToggle);
  }

  /* ---------- Header count badge --------------------------- */
  function updateCountTargets() {
    var count = SAWishlist.count();
    var nodes = document.querySelectorAll('[data-sa-wishlist-count]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var labelNode = node.querySelector('[data-sa-wishlist-count-value]') || node;
      labelNode.textContent = count < 100 ? String(count) : '99+';
      // Toggle visibility of the bubble itself when empty.
      if (count > 0) {
        node.removeAttribute('hidden');
        node.classList.remove('sa-hidden');
      } else {
        node.setAttribute('hidden', 'hidden');
        node.classList.add('sa-hidden');
      }
    }
  }

  /* ---------- Sync across all UI --------------------------- */
  function syncAllToggles() {
    var toggles = document.querySelectorAll('sa-wishlist-toggle');
    for (var i = 0; i < toggles.length; i++) {
      if (typeof toggles[i].render === 'function') toggles[i].render();
    }
  }

  document.addEventListener(CHANGE_EVENT, function () {
    updateCountTargets();
    syncAllToggles();
  });

  // Reflect changes made in other tabs/windows.
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) {
      updateCountTargets();
      syncAllToggles();
    }
  });

  // Initial paint. Runs whether or not the DOM is fully parsed yet; if the
  // header badge is not present at this point it will be reconciled by the
  // change/storage handlers, but the script is deferred so the parser is done.
  updateCountTargets();
})();
